import type { RoundEndBroadcastMsg } from "@hupper/shared/protocol";
import type { GameClient, MatchHudState } from "../net/GameClient";

/**
 * Top-of-screen MR7 scoreboard — CT score · centered round info · T score.
 *
 * Round info is the CS:GO-style block in the middle: a big mm:ss timer
 * sitting between the two scores, with a small "R<n> · phase" caption
 * beneath. The countdown anchors on every snapshot and interpolates with
 * requestAnimationFrame so the seconds digit ticks smoothly between
 * server messages. Last 10 s pulse red — same threshold as CS:GO's
 * "ten-second warning" beep.
 */
export class Scoreboard {
  private client: GameClient;
  private bar: HTMLDivElement;
  private scoreCt: HTMLSpanElement;
  private scoreT: HTMLSpanElement;
  private roundEl: HTMLSpanElement;
  private timeEl: HTMLSpanElement;
  private toast: HTMLDivElement;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly unsubs: Array<() => void> = [];

  /** Last server-reported "remaining seconds" + wall clock at that moment.
   *  Drives the per-frame countdown so the timer ticks smoothly even
   *  though snapshots only arrive at 32 Hz. */
  private anchorSeconds = 0;
  private anchorAtMs = 0;
  private anchorPhase = "";
  private rafHandle = 0;

  constructor(client: GameClient) {
    this.client = client;

    this.bar = document.createElement("div");
    this.bar.id = "scoreboard";
    this.bar.innerHTML = `
      <div class="sb-row">
        <span class="score-ct"></span>
        <div class="round-info">
          <span class="time">--:--</span>
          <span class="round"></span>
        </div>
        <span class="score-t"></span>
      </div>`;
    this.scoreCt = this.bar.querySelector(".score-ct") as HTMLSpanElement;
    this.roundEl = this.bar.querySelector(".round") as HTMLSpanElement;
    this.scoreT = this.bar.querySelector(".score-t") as HTMLSpanElement;
    this.timeEl = this.bar.querySelector(".time") as HTMLSpanElement;
    document.body.appendChild(this.bar);
    this.injectStyles();

    this.toast = document.createElement("div");
    this.toast.id = "round-toast";
    this.toast.className = "hidden";
    document.body.appendChild(this.toast);

    this.unsubs.push(this.client.onMatchHud((h) => this.renderHud(h)));
    this.unsubs.push(this.client.onRoundEnd((m) => this.showRoundToast(m)));

    // Smooth per-frame countdown — the snapshot anchor only ticks every
    // ~30 ms; the rAF loop is what makes the displayed digit decrement
    // visibly without waiting for the next server message.
    const tick = () => {
      this.renderTime();
      this.rafHandle = requestAnimationFrame(tick);
    };
    this.rafHandle = requestAnimationFrame(tick);
  }

  destroy(): void {
    cancelAnimationFrame(this.rafHandle);
    if (this.toastTimer) clearTimeout(this.toastTimer);
    for (const u of this.unsubs) u();
    this.bar.remove();
    this.toast.remove();
  }

  /** Snapshot-driven update — scores + round label + timer anchor. */
  private renderHud(h: MatchHudState): void {
    this.scoreCt.textContent = String(h.ctScore);
    this.scoreT.textContent = String(h.tScore);
    this.roundEl.textContent = `R${h.roundNumber} · ${h.phase.replace(/_/g, " ").toUpperCase()}`;

    let next = 0;
    if (h.phase === "buy" && typeof h.buySecondsLeft === "number") next = h.buySecondsLeft;
    else if (h.phase === "live" && typeof h.liveSecondsLeft === "number") next = h.liveSecondsLeft;

    if (h.phase !== this.anchorPhase) {
      // Phase change → snap to the new value, don't interpolate from a
      // stale anchor that would briefly flash the wrong digit.
      this.anchorPhase = h.phase;
      this.anchorSeconds = next;
      this.anchorAtMs = performance.now();
      return;
    }
    // Re-anchor if the server's clock drifted from our interpolation.
    const interpolated = this.currentRemaining();
    if (Math.abs(interpolated - next) > 1.2) {
      this.anchorSeconds = next;
      this.anchorAtMs = performance.now();
    }
  }

  private currentRemaining(): number {
    if (this.anchorPhase !== "buy" && this.anchorPhase !== "live") return 0;
    const elapsed = (performance.now() - this.anchorAtMs) / 1000;
    return Math.max(0, this.anchorSeconds - elapsed);
  }

  /** rAF — re-paint the mm:ss + critical/warning state. */
  private renderTime(): void {
    const remaining = this.currentRemaining();
    if (this.anchorPhase === "buy" || this.anchorPhase === "live") {
      const totalSec = Math.ceil(remaining);
      const mm = Math.floor(totalSec / 60);
      const ss = totalSec % 60;
      this.timeEl.textContent = `${mm}:${ss.toString().padStart(2, "0")}`;
    } else {
      this.timeEl.textContent = "--:--";
    }
    // CS:GO ten-second warning — turn red + pulse when the live phase
    // is in its final 10 s. Buy phase doesn't get the critical state;
    // running out the buy clock is non-fatal.
    const critical = this.anchorPhase === "live" && remaining < 10 && remaining > 0;
    const warning = this.anchorPhase === "live" && remaining < 20 && remaining >= 10;
    this.timeEl.classList.toggle("time-critical", critical);
    this.timeEl.classList.toggle("time-warning", warning);
  }

  private showRoundToast(m: RoundEndBroadcastMsg): void {
    const actor = m.winnerSessionId === this.client.sessionId ? "You" : "Opponent";
    const line1 = m.matchOver
      ? `Match over — ${actor} won (${m.ctScore}–${m.tScore})`
      : `${actor} won round ${m.roundCompleted}`;
    this.toast.innerHTML = `<div class="line1">${line1}</div><div class="line2">CT ${m.ctScore} — ${m.tScore} T</div>`;
    this.toast.classList.remove("hidden");
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => {
      this.toast.classList.add("hidden");
      this.toastTimer = null;
    }, 3200);
  }

  /** Override the index.html scoreboard CSS so the round-info column now
   *  hosts a CS:GO-style centered timer. Injects after the base styles
   *  so these selectors win. */
  private injectStyles(): void {
    if (document.getElementById("scoreboard-timer-styles")) return;
    const style = document.createElement("style");
    style.id = "scoreboard-timer-styles";
    style.textContent = `
      /* The round-info column is the CS:GO middle block — big timer on
         top, tiny "R12 · BUY" caption underneath. */
      #scoreboard .round-info {
        min-width: 92px;
        padding: 4px 22px;
        gap: 2px;
      }
      #scoreboard .round-info .time {
        font-family: 'Geist Mono', ui-monospace, SFMono-Regular, monospace;
        font-size: 26px;
        font-weight: 800;
        line-height: 1;
        color: #ffffff;
        letter-spacing: -0.02em;
        font-variant-numeric: tabular-nums;
        text-shadow: 1px 1px 0 rgba(0,0,0,0.85);
        transition: color 140ms ease;
      }
      #scoreboard .round-info .round {
        font-family: 'Geist Mono', ui-monospace, monospace;
        font-size: 9.5px;
        font-weight: 700;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: rgba(255, 255, 255, 0.55);
      }
      /* Last 20 s — amber. Last 10 s (CS:GO ten-second warning) — red
         pulse. Buy phase never enters either state; only live counts down. */
      #scoreboard .round-info .time.time-warning {
        color: #ffcb6b;
      }
      #scoreboard .round-info .time.time-critical {
        color: #ff4d4d;
        animation: sb-time-pulse 0.6s ease-in-out infinite;
      }
      @keyframes sb-time-pulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.6; transform: scale(0.96); }
      }
    `;
    document.head.appendChild(style);
  }
}
